import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import React, { useEffect, useState } from 'react'
import { Grid, Stack, Typography, Card, Button, CardContent, Accordion, AccordionSummary } from '@mui/material'
import StepperDialog from 'components/modal/StepperDialog'
import PresentationCard from 'components/card/PresentationCard'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import { CardTable } from 'components/datatable/NewTable'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { CardTitle } from 'components/card/NewCard'
import moment from 'moment'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AccordionDangerTable } from './DangerTable'
import useSandreList from 'utils/customHook/useSandreList'
import { nbPerPageLabelMedium, SANDRE } from 'referencial/constants/ReferencialConstants'
import { getI18nTitleDataLength } from 'utils/StringUtil'
import PgsseAction from 'pgsse/action/PgsseAction'
import Spinner from 'components/progress/Spinner'
import { getFullDate } from 'utils/DateUtil'
import DtoDangerTable from 'pgsse/dto/DtoDangerTable'
import DtoPgsseImport from 'pgsse/dto/DtoPgsseImport'
import { groupBy, keys, orderBy } from 'lodash'
import { getLabel } from 'utils/StoreUtils'

const STEP = {
    STEP_1: 0,
    STEP_2: 1,
    STEP_3: 2,
    STEP_4: 3,
}

const IMPORT = 'import'
const DELETE = 'delete'
const REPLACE = 'replace'
const VISUALIZE = 'visualize'

const AccordionDangers = ({ dangers }) => {
    const structures = useSandreList(SANDRE.PGSSE_STRUCTURE)
    const group = groupBy(orderBy(dangers, 'idStructure'), 'idStructure')

    return (
        <Grid
            container
            spacing={1}
            direction='column'
        >
            {keys(group).map((key, i) => {
                const filteredDangers = group[key]
                return (
                    <Accordion
                        key={getLabel(structures, parseInt(key))}
                        defaultExpanded={i===0}
                    >
                        <AccordionSummary>{`${getLabel(structures, parseInt(key))} (${filteredDangers.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, filteredDangers.length)})`}</AccordionSummary>
                        <AccordionDangerTable dangers={filteredDangers} />
                    </Accordion>
                )
            })}
        </Grid>
    )
}

AccordionDangers.propTypes = {
    dangers: PropTypes.arrayOf(PropTypes.instanceOf(DtoDangerTable)),
}

const ImportDangerStep1 = ({
    nextStep = () => {},
    setType = () => {},
    idDiag,
    phase,
}) => {
    const dispatch = useDispatch()
    return (
        <Stack spacing={1} sx={{ padding: '0 4rem' }}>
            <Grid container justifyContent='center' alignItems='center'>
                <h4>{i18n.chooseAction}</h4>
            </Grid>
            <PresentationCard
                iconComponent={<FileUploadIcon sx={{ fontSize: '4rem' }} />}
                title={i18n.importLabel}
                onClick={() => {
                    nextStep()
                    setType(IMPORT)
                }}
                description={i18n.importDangersDescription}
            />
            <PresentationCard
                iconComponent={<DeleteIcon sx={{ fontSize: '4rem' }} />}
                title={i18n.delete}
                onClick={() => {
                    dispatch(PgsseAction.fetchImports(idDiag, phase)).then(() => nextStep())
                    setType(DELETE)
                }}
                description={i18n.deleteDangersDescription}
            />
            <PresentationCard
                iconComponent={<SwapHorizIcon sx={{ fontSize: '4rem' }} />}
                title={i18n.replace}
                onClick={() => {
                    dispatch(PgsseAction.fetchImports(idDiag, phase)).then(() => nextStep())
                    setType(REPLACE)
                }}
                description={i18n.replaceDangersDescription}
            />
            <PresentationCard
                iconComponent={<VisibilityIcon sx={{ fontSize: '4rem' }} />}
                title={i18n.visualize}
                onClick={() => {
                    dispatch(PgsseAction.fetchImports(idDiag, phase)).then(() => nextStep())
                    setType(VISUALIZE)
                }}
                description={i18n.visualizeDangersDescription}
            />
        </Stack>
    )
}

ImportDangerStep1.propTypes = {
    nextStep: PropTypes.func,
    setType: PropTypes.func,
    idDiag: PropTypes.number,
    phase: PropTypes.number,
}

const ImportDangerStep2 = ({
    type,
    nextStep,
    setStep3Ready,
    selectedImport,
    imports,
    setNewImport,
    setSelectedImport,
    importDangers,
}) => {
    const dispatch = useDispatch()
    const {
        accountUser,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const onUploadFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        if (file) {
            reader.onloadend = (event) => {
                const split = event.target.result.split(',')
                setNewImport({
                    name: file.name,
                    fileContent: split[1],
                    date: moment().format('DD/MM/YYYY'), // TODO
                    login: accountUser?.login,
                })
            }
            reader.readAsDataURL(file)
            setStep3Ready(true)
        }
    }

    const headers = ['fileName', 'updateDate', 'updateLogin']

    const onSelectElement = (row) => {
        setSelectedImport(row)
        dispatch(PgsseAction.fetchImportDangers(row.id))
        nextStep()
    }

    switch (type) {
        case IMPORT:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.importFile}</h4>
                    </Grid>
                    <Card>
                        <CardTitle title={i18n.importXLSOrCSVFile} />
                        <CardContent>
                            <Grid size={7}>
                                <div className='file-field input-field'>
                                    <div className='btn'>
                                        <span>{i18n.importLabel}</span>
                                        <input
                                            accept='.xls, .xlsx, .csv'
                                            type='file'
                                            onChange={onUploadFile}
                                        />
                                    </div>
                                    <div className='file-path-wrapper'>
                                        <input className='file-path validate' type='text' />
                                    </div>
                                </div>
                            </Grid>
                        </CardContent>
                    </Card>
                </Stack>
            )
        case DELETE:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.deleteImport}</h4>
                    </Grid>
                    <CardTable
                        title={i18n.selectImportToDelete}
                        headers={headers}
                        rows={imports}
                        onClickRow={onSelectElement}
                    />
                </Stack>
            )
        case REPLACE:
            return (
                <Stack spacing={1}>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.replaceImport}</h4>
                    </Grid>
                    {!selectedImport?.id ? (
                        <CardTable
                            title={i18n.selectImportToReplace}
                            headers={headers}
                            rows={imports}
                            onClickRow={(row) => {
                                setSelectedImport(row)
                                dispatch(PgsseAction.fetchImportDangers(row.id))
                            }}
                        />
                    ) : (
                        <>
                            <Card>
                                <CardTitle title={i18n.importXLSOrCSVFile} />
                                <CardContent>
                                    <Grid size={7}>
                                        <div className='file-field input-field'>
                                            <div className='btn'>
                                                <span>{i18n.importLabel}</span>
                                                <input
                                                    accept='.xls, .xlsx, .csv'
                                                    type='file'
                                                    onChange={onUploadFile}
                                                />
                                            </div>
                                            <div className='file-path-wrapper'>
                                                <input className='file-path validate' type='text' />
                                            </div>
                                        </div>
                                    </Grid>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent>
                                    <Typography>{i18n.confirmDeletionDescription}</Typography>
                                    <AccordionDangers dangers={importDangers} />
                                </CardContent>
                            </Card>
                        </>
                    )}
                </Stack>
            )
        case VISUALIZE:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.visualizeImport}</h4>
                    </Grid>
                    <CardTable
                        title={i18n.selectImportToVisualize}
                        headers={headers}
                        rows={imports}
                        onClickRow={onSelectElement}
                    />
                </Stack>
            )
        default:
            return null
    }
}

ImportDangerStep2.propTypes = {
    type: PropTypes.string,
    nextStep: PropTypes.func,
    setStep3Ready: PropTypes.func,
    setNewImport: PropTypes.func,
    imports: PropTypes.arrayOf(PropTypes.instanceOf(DtoPgsseImport)),
    selectedImport: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        date: PropTypes.string,
        login: PropTypes.string,
    }),
    setSelectedImport: PropTypes.func,
    importDangers: PropTypes.arrayOf(PropTypes.instanceOf(DtoDangerTable)),
}

const ImportDangerStep3 = ({
    type,
    nextStep,
    selectedImport,
    errors,
    dataLoaded,
    importSuccess,
    importDangers,
}) => {
    useEffect(() => {
        if ((type === IMPORT || type === REPLACE) && importSuccess) {
            nextStep()
        }
    }, [type, importSuccess, nextStep])

    switch (type) {
        case IMPORT:
        case REPLACE:
            return dataLoaded ? (
                <Grid justifyContent='center' alignItems='center' paddingTop={2}>
                    <CardTable
                        title={i18n.errors}
                        rows={errors}
                        headers={['code', 'error']}
                        rowsPerPageOptions={ nbPerPageLabelMedium }
                    />
                </Grid>
            ) : (
                <Card>
                    <CardContent>
                        <Stack spacing={2} justifyContent='center' alignItems='center'>
                            <h4>{i18n.jobProgressMessage}</h4>
                            <h5>{i18n.jobProgressDescription}</h5>
                            <Spinner />
                        </Stack>
                    </CardContent>
                </Card>
            )
        case DELETE:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.confirmDeletion}</h4>
                    </Grid>
                    <Card>
                        <CardContent>
                            <Typography>{i18n.confirmDeletionDescription}</Typography>
                            <AccordionDangers dangers={importDangers} />
                        </CardContent>
                    </Card>
                </Stack>
            )
        case VISUALIZE:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.visualisation} : {selectedImport?.name}</h4>
                    </Grid>
                    <Card>
                        <CardContent>
                            <AccordionDangers dangers={importDangers} />
                        </CardContent>
                    </Card>
                </Stack>
            )
        default:
            return null
    }
}

ImportDangerStep3.propTypes = {
    type: PropTypes.string,
    nextStep: PropTypes.func,
    prevStep: PropTypes.func,
    selectedImport: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        date: PropTypes.string,
        login: PropTypes.string,
    }),
    errors: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string,
        error: PropTypes.string,
    })),
    dataLoaded: PropTypes.bool,
    importSuccess: PropTypes.bool,
    importCancel: PropTypes.bool,
    importDangers: PropTypes.arrayOf(PropTypes.instanceOf(DtoDangerTable)),
}

const ImportDangerStep4 = ({
    type,
    importDangers,
}) => {
    switch (type) {
        case IMPORT:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.importResult}</h4>
                    </Grid>
                    <Card>
                        <CardContent>
                            <AccordionDangers dangers={importDangers} />
                        </CardContent>
                    </Card>
                </Stack>
            )
        case REPLACE:
            return (
                <Stack>
                    <Grid container justifyContent='center' alignItems='center'>
                        <h4>{i18n.replaceResult}</h4>
                    </Grid>
                    <Card>
                        <CardContent>
                            <Typography>{i18n.replaceSuccess}</Typography>
                            <AccordionDangers dangers={importDangers} />
                        </CardContent>
                    </Card>
                </Stack>
            )
        default:
            return null
    }
}

ImportDangerStep4.propTypes = {
    type: PropTypes.string,
    importDangers: PropTypes.arrayOf(PropTypes.instanceOf(DtoDangerTable)),
}

const ImportDangerDialog = ({
    open,
    setIsOpen,
    phase,
    idDiag,
}) => {
    const dispatch = useDispatch()

    const {
        imports,
        importDangers,
    } = useSelector(store => ({
        imports: store.PgsseReducer.imports,
        importDangers: store.PgsseReducer.importDangers,
    }))

    const [selectedImport, setSelectedImport] = useState()
    const [type, setType] = useState('')
    const [newImport, setNewImport] = useState(null)
    const [step3Ready, setStep3Ready] = useState(false)
    const [step4Ready, setStep4Ready] = useState(false)
    const [errors, setErrors] = useState([])
    const [dataLoaded, setDataLoaded] = useState(false)
    const [importSuccess, setImportSuccess] = useState(false)

    const onImport = (importAction) => {
        if (!newImport) return

        setErrors([])
        setDataLoaded(false)
        setImportSuccess(false)
        setStep4Ready(false)

        const body = {
            importData: {
                idDiag,
                phase,
                fileName: newImport.name,
                updateLogin: newImport.login,
                updateDate: moment().valueOf(),
            },
            fileContent: newImport.fileContent,
            idReplace: selectedImport?.id,
        }

        dispatch(importAction(body)).then(data => {
            if (data.status === 'error') {
                setDataLoaded(true)
                setErrors(data.logs)
            } else {
                dispatch(PgsseAction.fetchDiagnosticDangers(idDiag))
                setImportSuccess(true)
                setStep4Ready(true)
            }
        })
    }

    const onClose = () => {
        setIsOpen(false)
        setStep3Ready(false)
        setStep4Ready(false)
        setNewImport(null)
        setSelectedImport(undefined)
        setErrors([])
        setDataLoaded(false)
        setImportSuccess(false)
    }

    const onDelete = () => {
        dispatch(PgsseAction.deleteImport(selectedImport.id)).then(() => {
            dispatch(PgsseAction.fetchDiagnosticDangers(idDiag))
        })
        onClose()
    }

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.choiceOfAction,
                    constant: STEP.STEP_1,
                    nextAvailable: false,
                },
                {
                    label: i18n.importName,
                    constant: STEP.STEP_2,
                    nextAvailable: step3Ready,
                    onNext: (goNext) => {
                        if (type === IMPORT) {
                            onImport(PgsseAction.importDangers)
                        }
                        if (type === REPLACE) {
                            onImport(PgsseAction.replaceImport)
                        }
                        goNext()
                    },
                    onPrev: (prevStep) => {
                        if (type === REPLACE && selectedImport?.id) {
                            setSelectedImport({})
                        } else {
                            setSelectedImport(undefined)
                            setStep3Ready(false)
                            setNewImport(null)
                            prevStep()
                        }
                    },
                },
                {
                    label: type === VISUALIZE ? i18n.visualisation : i18n.actionInProgress,
                    constant: STEP.STEP_3,
                    onNext: (goNext) => {
                        goNext()
                    },
                    onPrev: (prevStep) => {
                        setStep3Ready(false)
                        setStep4Ready(false)
                        prevStep()
                    },
                    nextAvailable: step4Ready,
                },
                {
                    previousStep: STEP.STEP_2,
                    label: i18n.actionResult,
                    constant: STEP.STEP_4,
                    onNext: onClose,
                    nextAvailable: true,
                    nextLabel: i18n.finish,
                },
            ]}
            open={open}
            title={i18n.importDangers}
            closeDialog={onClose}
            renderSaveButton={(step) => {
                if (type === DELETE && step === STEP.STEP_3) {
                    return (
                        <Button
                            variant='outlined'
                            color='error'
                            onClick={onDelete}
                        >
                            {i18n.delete}
                        </Button>
                    )
                }
                return null
            }}
        >
            {(step, nextStep, prevStep) => {
                const params = { type, setType, open, nextStep, prevStep, setIsOpen, selectedImport, setSelectedImport, idDiag, phase, importDangers }
                switch (step) {
                    case STEP.STEP_1:
                        return <ImportDangerStep1 {...params}/>
                    case STEP.STEP_2:
                        return (
                            <ImportDangerStep2
                                setStep3Ready={setStep3Ready}
                                setNewImport={setNewImport}
                                imports={imports.map(d => ({ ...d, updateDate: getFullDate(d.updateDate) }))}
                                {...params}
                            />
                        )
                    case STEP.STEP_3:
                        return (
                            <ImportDangerStep3
                                errors={errors}
                                dataLoaded={dataLoaded}
                                importSuccess={importSuccess}
                                {...params}
                            />
                        )
                    case STEP.STEP_4:
                        return <ImportDangerStep4 {...params}/>
                    default:
                        return null
                }
            }}
        </StepperDialog>
    )
}

ImportDangerDialog.propTypes = {
    open: PropTypes.bool,
    setIsOpen: PropTypes.func,
    phase: PropTypes.number,
    idDiag: PropTypes.number,
}

export default ImportDangerDialog
